import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { handleApiError } from '../../utils/route-guard/errorHandling';

// Existing fetchComparables thunk
export const fetchComparables = createAsyncThunk('comparables/fetchComparables', async ({ url, token, postData }, { rejectWithValue }) => {
  try {
    const response = await axios.post(url, postData, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: token
      }
    });
    return response.data;
  } catch (error) {
    handleApiError(error);
    return rejectWithValue(error.response ? error.response.data : error.message);
  }
});

// Search the authenticated user's own uploaded transactions (non-TRIAL plans only)
export const fetchUserTransactionsComparables = createAsyncThunk(
  'comparables/fetchUserTransactionsComparables',
  async ({ url, token, postData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(url, postData, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: token
        }
      });
      // extra_data is JSONB (unordered on retrieval), so the upload-time field order comes
      // back separately from the schema registry — carry both through together, plus the
      // original (pre-normalisation) header text for display.
      return {
        list: response.data.data,
        columns: response.data.columns || [],
        columnLabels: response.data.column_labels || {},
        // true when the result set hit MAX_FILTER_RESULTS server-side (see
        // filter_user_transactions) -- the frontend must say so rather than showing what looks
        // like a complete result set that's actually silently missing rows.
        truncated: Boolean(response.data.truncated)
      };
    } catch (error) {
      handleApiError(error);
      return rejectWithValue(error.response ? error.response.data : error.message);
    }
  }
);

// Min/max transaction year available per (source, dateField) combination, so the year-range
// filter can be sized to the real data instead of a hardcoded range. Fetched at most once per
// cacheKey per session (see useComparablesForm.js) — it rarely changes within a session, so
// this is kept separate from resetComparables below rather than being cleared on every new
// property view. dateField only affects the user_transactions source (a row there carries both
// an Acceptance and an Agreement date); it's ignored server-side for 'db'.
export const fetchYearRange = createAsyncThunk(
  'comparables/fetchYearRange',
  async ({ source, dateField, token, cacheKey }, { rejectWithValue }) => {
    try {
      const dateFieldParam = source === 'user_transactions' && dateField ? `&date_field=${dateField}` : '';
      const response = await axios.get(`/api/transactions/year_range?source=${source}${dateFieldParam}`, {
        headers: { Authorization: token }
      });
      return { cacheKey: cacheKey || source, ...response.data.data };
    } catch (error) {
      handleApiError(error);
      return rejectWithValue(error.response ? error.response.data : error.message);
    }
  }
);

const initialState = {
  comparablesList: null,
  status: 'idle',
  error: null,
  source: null,
  columnOrder: [], // upload-time field order for the user_transactions source (see filter_user_transactions)
  columnLabels: {}, // normalised field name -> original uploaded header text, user_transactions only
  truncated: false, // true when the last user_transactions search hit MAX_FILTER_RESULTS
  yearRangeBySource: {} // { db: {minYear, maxYear}, user_transactions: {minYear, maxYear} }
};

export const comparablesSlice = createSlice({
  name: 'comparables',
  initialState,
  reducers: {
    // yearRangeBySource is a session-long cache independent of any particular search's
    // results, so it survives a reset (e.g. navigating to a different property) rather than
    // forcing a re-fetch every time.
    resetComparables: (state) => ({ ...initialState, yearRangeBySource: state.yearRangeBySource })
  },
  extraReducers: (builder) => {
    builder
      // fetchComparables cases
      .addCase(fetchComparables.pending, (state) => {
        state.status = 'loading';
        state.source = 'db';
        state.truncated = false;
      })
      .addCase(fetchComparables.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.comparablesList = action.payload.data;
        state.source = 'db';
      })
      .addCase(fetchComparables.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
        state.source = 'db';
      })

      // fetchUserTransactionsComparables cases
      .addCase(fetchUserTransactionsComparables.pending, (state) => {
        state.status = 'loading';
        state.source = 'user_transactions';
        state.truncated = false;
      })
      .addCase(fetchUserTransactionsComparables.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.comparablesList = action.payload.list;
        state.columnOrder = action.payload.columns;
        state.columnLabels = action.payload.columnLabels;
        state.truncated = action.payload.truncated;
        state.source = 'user_transactions';
      })
      .addCase(fetchUserTransactionsComparables.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
        state.source = 'user_transactions';
      })

      // fetchYearRange cases — deliberately don't touch status/error, since this is a
      // background cache-fill, not the user-facing search itself. 'pending' marks the cache
      // key as in-flight so useComparablesForm.js's cache check doesn't fire a duplicate
      // request.
      .addCase(fetchYearRange.pending, (state, action) => {
        state.yearRangeBySource[action.meta.arg.cacheKey || action.meta.arg.source] = 'loading';
      })
      .addCase(fetchYearRange.fulfilled, (state, action) => {
        const { cacheKey, min_year: minYear, max_year: maxYear } = action.payload;
        state.yearRangeBySource[cacheKey] = { minYear, maxYear };
      })
      .addCase(fetchYearRange.rejected, (state, action) => {
        // Clear the in-flight marker so a later toggle back to this cache key can retry.
        delete state.yearRangeBySource[action.meta.arg.cacheKey || action.meta.arg.source];
      });
  }
});

// Remove unused actions if any
// export const { updateDisplayStatus } = comparablesSlice.actions;
export const { resetComparables } = comparablesSlice.actions;

export default comparablesSlice.reducer;
